import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Table, Spinner, Badge, Pagination, ProgressBar } from 'react-bootstrap';
import { getApiUrl } from '../config/api';

// Daftar Lokasi Gudang sesuai instruksi user:
// 00001 = Apotek Rawat Jalan, 00002 = Apotek Rawat Inap, 00005 = Apotek Orange, 00036 = Gudang SILK, 00004 = Gudang PBF
const LOKASI_OPTIONS = [
  { kode: '00001', nama: 'Apotek Rawat Jalan' },
  { kode: '00002', nama: 'Apotek Rawat Inap' },
  { kode: '00005', nama: 'Apotek Orange' },
  { kode: '00036', nama: 'Gudang SILK' },
  { kode: '00004', nama: 'Gudang PBF' },
];

// Daftar Jenis Laporan sesuai instruksi user:
// 1 = Laporan Penjualan & Retur, 2 = Laporan Pembelian & Retur
const JENIS_LAPORAN_OPTIONS = [
  { kode: '1', nama: 'Laporan Penjualan & Retur' },
  { kode: '2', nama: 'Laporan Pembelian & Retur' },
];

// Helper konversi format tanggal YYYY-MM-DD ke DD/MM/YYYY (sesuai kebutuhan SQL backend %d/%m/%Y)
const toBackendDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return '';
  const parts = yyyyMmDd.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return yyyyMmDd;
};

// Helper format angka rupiah
const fmtRupiah = (val) => {
  const n = parseFloat(val);
  if (isNaN(n)) return '0';
  return n.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

// Helper format tanggal display DD/MM/YYYY
const fmtDateDisplay = (dateStr) => {
  if (!dateStr) return '-';
  const parts = dateStr.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dateStr;
};

// Mock data representatif untuk Laporan Penjualan & Retur (Jenis = 1)
const MOCK_PENJUALAN_DATA = [
  { id: 1, no_bukti: 'PJ-2610-0012', tanggal: '2026-10-21', no_rm: '00-34-11', nama_pasien: 'Bpk. Ahmad Suhendar', dokter: 'dr. H. Rahmat, Sp.PD', tipe: 'Penjualan', cara_bayar: 'Tunai', bruto: 425000, diskon: 0, retur: 0, netto: 425000, kasir: 'Apt. Sarah' },
  { id: 2, no_bukti: 'PJ-2610-0013', tanggal: '2026-10-21', no_rm: '00-56-82', nama_pasien: 'Ibu Ratna Dewi', dokter: 'dr. Anita S., Sp.A', tipe: 'Penjualan', cara_bayar: 'BPJS Kesehatan', bruto: 285000, diskon: 0, retur: 0, netto: 285000, kasir: 'Apt. Sarah' },
  { id: 3, no_bukti: 'RT-2610-0004', tanggal: '2026-10-21', no_rm: '00-34-11', nama_pasien: 'Bpk. Ahmad Suhendar (Retur Salep)', dokter: 'dr. H. Rahmat, Sp.PD', tipe: 'Retur', cara_bayar: 'Tunai', bruto: 0, diskon: 0, retur: 75000, netto: -75000, kasir: 'Apt. Lastry' },
  { id: 4, no_bukti: 'PJ-2610-0014', tanggal: '2026-10-22', no_rm: '00-88-29', nama_pasien: 'Ny. Siti Aminah', dokter: 'dr. Bambang, Sp.OG', tipe: 'Penjualan', cara_bayar: 'Debit BCA', bruto: 760000, diskon: 20000, retur: 0, netto: 740000, kasir: 'Apt. Sarah' },
  { id: 5, no_bukti: 'PJ-2610-0015', tanggal: '2026-10-22', no_rm: '00-12-90', nama_pasien: 'Sdr. Dimas Pratama', dokter: 'dr. Hendra, Sp.JP', tipe: 'Penjualan', cara_bayar: 'Tunai', bruto: 540000, diskon: 0, retur: 0, netto: 540000, kasir: 'Apt. Rudi' },
  { id: 6, no_bukti: 'PJ-2610-0016', tanggal: '2026-10-22', no_rm: '00-77-43', nama_pasien: 'Ibu Nurbaiti', dokter: 'dr. H. Rahmat, Sp.PD', tipe: 'Penjualan', cara_bayar: 'Asuransi Mandiri Inhealth', bruto: 980000, diskon: 0, retur: 0, netto: 980000, kasir: 'Apt. Rudi' },
  { id: 7, no_bukti: 'RT-2610-0005', tanggal: '2026-10-22', no_rm: '00-77-43', nama_pasien: 'Ibu Nurbaiti (Retur Alergi Obat)', dokter: 'dr. H. Rahmat, Sp.PD', tipe: 'Retur', cara_bayar: 'Asuransi Mandiri Inhealth', bruto: 0, diskon: 0, retur: 120000, netto: -120000, kasir: 'Apt. Rudi' },
  { id: 8, no_bukti: 'PJ-2610-0017', tanggal: '2026-10-22', no_rm: '00-91-05', nama_pasien: 'Tn. Hendra Wijaya', dokter: 'dr. Farida, Sp.M', tipe: 'Penjualan', cara_bayar: 'Tunai', bruto: 315000, diskon: 0, retur: 0, netto: 315000, kasir: 'Apt. Sarah' },
];

// Mock data representatif untuk Laporan Pembelian & Retur (Jenis = 2)
const MOCK_PEMBELIAN_DATA = [
  { id: 1, no_faktur: 'FAK-PBF-0891', no_sp: 'SP-APTK-2610/01', tanggal: '2026-10-18', kode_pbf: 'PBF-001', nama_pbf: 'PT. Enseval Putera Megatrading', tipe: 'Pembelian', jatuh_tempo: '2026-11-18', subtotal: 12500000, ppn: 1375000, retur: 0, netto: 13875000, status_bayar: 'Belum Lunas' },
  { id: 2, no_faktur: 'FAK-PBF-0892', no_sp: 'SP-APTK-2610/02', tanggal: '2026-10-19', kode_pbf: 'PBF-002', nama_pbf: 'PT. Kimia Farma Trading & Distribution', tipe: 'Pembelian', jatuh_tempo: '2026-11-19', subtotal: 8900000, ppn: 979000, retur: 0, netto: 9879000, status_bayar: 'Belum Lunas' },
  { id: 3, no_faktur: 'RTB-2610-001', no_sp: 'SP-APTK-2610/01', tanggal: '2026-10-20', kode_pbf: 'PBF-001', nama_pbf: 'PT. Enseval Putera Megatrading', tipe: 'Retur Beli', jatuh_tempo: '-', subtotal: 0, ppn: 0, retur: 1450000, netto: -1450000, status_bayar: 'Kredit Nota' },
  { id: 4, no_faktur: 'FAK-PBF-0893', no_sp: 'SP-APTK-2610/03', tanggal: '2026-10-20', kode_pbf: 'PBF-003', nama_pbf: 'PT. Mensa Bina Sukses (MBS)', tipe: 'Pembelian', jatuh_tempo: '2026-11-20', subtotal: 15200000, ppn: 1672000, retur: 0, netto: 16872000, status_bayar: 'Lunas' },
  { id: 5, no_faktur: 'FAK-PBF-0894', no_sp: 'SP-APTK-2610/04', tanggal: '2026-10-21', kode_pbf: 'PBF-004', nama_pbf: 'PT. Anugrah Argon Medica (AAM)', tipe: 'Pembelian', jatuh_tempo: '2026-11-21', subtotal: 6700000, ppn: 737000, retur: 0, netto: 7437000, status_bayar: 'Belum Lunas' },
  { id: 6, no_faktur: 'RTB-2610-002', no_sp: 'SP-APTK-2610/03', tanggal: '2026-10-22', kode_pbf: 'PBF-003', nama_pbf: 'PT. Mensa Bina Sukses (MBS)', tipe: 'Retur Beli', jatuh_tempo: '-', subtotal: 0, ppn: 0, retur: 850000, netto: -850000, status_bayar: 'Kredit Nota' },
];

const LapApotekFinance = () => {
  const navigate = useNavigate();

  // Inisialisasi tanggal awal bulan & hari ini
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const defaultAwal = `${yyyy}-${mm}-01`;
  const defaultAkhir = `${yyyy}-${mm}-${dd}`;

  // Form Filter State
  const [lokasiGudang, setLokasiGudang] = useState('00001');
  const [jenisLaporan, setJenisLaporan] = useState('1');
  const [periodeAwal, setPeriodeAwal] = useState(defaultAwal);
  const [periodeAkhir, setPeriodeAkhir] = useState(defaultAkhir);

  // Status Proses & Progress Bar
  const [isLoading, setIsLoading] = useState(false);
  const [progressVal, setProgressVal] = useState(0);
  const [progressStatusText, setProgressStatusText] = useState('');

  // Error Handling State (Refactored)
  const [errorMsg, setErrorMsg] = useState('');
  const [errorDetails, setErrorDetails] = useState(null);

  // Info Hasil Unduhan Excel
  const [downloadSuccess, setDownloadSuccess] = useState(false);
  const [downloadedFileInfo, setDownloadedFileInfo] = useState(null);
  const downloadUrlRef = useRef(null);

  // State Data Riil dari Backend
  const [reportData, setReportData] = useState([]);
  const [backendSummary, setBackendSummary] = useState(null);

  // Status Tampilan Data Preview
  const [hasProcessed, setHasProcessed] = useState(false);
  const [activeReportType, setActiveReportType] = useState('1');
  const [activeLokasi, setActiveLokasi] = useState('00001');
  const [searchFilter, setSearchFilter] = useState('');
  const [filterTipe, setFilterTipe] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 15;

  // Header Time & User
  const [time, setTime] = useState({ date: '', clock: '' });
  const [username, setUsername] = useState('Apoteker / Finance');

  useEffect(() => {
    try {
      const savedUser = localStorage.getItem('nama_user') || localStorage.getItem('user');
      if (savedUser) setUsername(savedUser);
    } catch (e) {}

    const updateTime = () => {
      const now = new Date();
      setTime({
        date: now.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }),
        clock: now.toLocaleTimeString('id-ID') + ' WIB'
      });
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Cleanup object URL saat unmount
  useEffect(() => {
    return () => {
      if (downloadUrlRef.current) {
        window.URL.revokeObjectURL(downloadUrlRef.current);
      }
    };
  }, []);

  // Handler Unduh Langsung via Browser Tab / Window (Bypass Fetch / Streaming Restriction)
  const handleDirectDownload = () => {
    const isJenis1 = String(jenisLaporan) === '1';
    const endpointName = isJenis1 ? 'lapjual_apotek' : 'lapbeli_apotek';
    const p1 = toBackendDate(periodeAwal);
    const p2 = toBackendDate(periodeAkhir);
    const baseUrl = getApiUrl() || 'http://localhost:8000';
    const fullUrl = `${baseUrl}/${endpointName}?loklap=${encodeURIComponent(lokasiGudang)}&periode1=${encodeURIComponent(p1)}&periode2=${encodeURIComponent(p2)}`;
    window.open(fullUrl, '_blank');
  };

  // Handler Utama Proses Laporan:
  // - Panggil backend untuk memuat data rincian transaksi obat ke tabel (JSON)
  // - Sekaligus unduh berkas Excel resmi (.xlsx)
  // - Refactoring error handling dengan pelaporan detail
  const handleProsesLaporan = async () => {
    setErrorMsg('');
    setErrorDetails(null);
    setDownloadSuccess(false);

    const isJenis1 = String(jenisLaporan) === '1';
    const endpointExcel = isJenis1 ? 'lapjual_apotek' : 'lapbeli_apotek';
    const endpointData = isJenis1 ? 'lapjual_apotek_data' : 'lapbeli_apotek_data';
    const reportTitle = isJenis1 ? 'Penjualan & Retur Obat' : 'Pembelian & Retur Obat';

    setIsLoading(true);
    setProgressVal(15);
    setProgressStatusText(`Menghubungkan ke backend untuk mengambil data ${reportTitle}...`);

    const p1 = toBackendDate(periodeAwal);
    const p2 = toBackendDate(periodeAkhir);
    const baseUrl = getApiUrl() || 'http://localhost:8000';
    const dataUrl = `${baseUrl}/${endpointData}?loklap=${encodeURIComponent(lokasiGudang)}&periode1=${encodeURIComponent(p1)}&periode2=${encodeURIComponent(p2)}`;
    const excelUrl = `${baseUrl}/${endpointExcel}?loklap=${encodeURIComponent(lokasiGudang)}&periode1=${encodeURIComponent(p1)}&periode2=${encodeURIComponent(p2)}`;

    // Timer animasi progres
    const progressInterval = setInterval(() => {
      setProgressVal((prev) => {
        if (prev < 45) return prev + 10;
        if (prev < 75) return prev + 5;
        if (prev < 88) return prev + 2;
        return prev;
      });
      setProgressStatusText((prevText) => {
        return `Sedang memproses rincian transaksi ${reportTitle} gudang apotek...`;
      });
    }, 450);

    try {
      // 1. Ambil data rincian JSON dari backend untuk mengisi tabel & kartu KPI
      setProgressVal(40);
      setProgressStatusText(`Mengambil data rincian dari backend (${endpointData})...`);

      const resData = await fetch(dataUrl, { method: 'GET' });
      if (!resData.ok) {
        let errText = '';
        try {
          const ej = await resData.json();
          errText = ej.msg || ej.error || JSON.stringify(ej);
        } catch (_) {
          errText = await resData.text();
        }
        const cleanErr = errText.replace(/<[^>]*>?/gm, ' ').replace(/\s+/g, ' ').trim();
        throw new Error(`Gagal memuat data tabel (${resData.status}): ${cleanErr.slice(0, 200)}`);
      }

      const jsonResult = await resData.json();
      if (jsonResult.status === 0 && !jsonResult.data) {
        throw new Error(jsonResult.msg || 'Backend mengembalikan kegagalan pengambilan data.');
      }

      const rows = Array.isArray(jsonResult.data) ? jsonResult.data : [];
      setReportData(rows);
      setBackendSummary(jsonResult.summary || null);
      setActiveReportType(jenisLaporan);
      setActiveLokasi(lokasiGudang);
      setHasProcessed(true);
      setCurrentPage(1);

      // 2. Ambil berkas Excel untuk download otomatis
      setProgressVal(85);
      setProgressStatusText('Menyiapkan dan mengunduh berkas Excel (.xlsx)...');

      const resExcel = await fetch(excelUrl, { method: 'GET' });
      if (resExcel.ok) {
        const blob = await resExcel.blob();
        if (blob && blob.size > 0 && !blob.type.includes('application/json')) {
          let fileName = '';
          const disposition = resExcel.headers.get('content-disposition');
          if (disposition && disposition.includes('filename=')) {
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            if (match && match[1]) {
              fileName = match[1].replace(/['"]/g, '').trim();
            }
          }
          if (!fileName) {
            const prefix = isJenis1 ? 'lapjual_apotek' : 'lapbeli_apotek';
            fileName = `${prefix}_${lokasiGudang}_${p1.replace(/\//g, '')}_${p2.replace(/\//g, '')}.xlsx`;
          }

          if (downloadUrlRef.current) {
            window.URL.revokeObjectURL(downloadUrlRef.current);
          }
          const dlUrl = window.URL.createObjectURL(blob);
          downloadUrlRef.current = dlUrl;

          const a = document.createElement('a');
          a.href = dlUrl;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          const lokasiObj = LOKASI_OPTIONS.find(l => l.kode === lokasiGudang);
          setDownloadedFileInfo({
            fileName,
            sizeKb: (blob.size / 1024).toFixed(1),
            lokasi: lokasiObj ? `${lokasiObj.kode} = ${lokasiObj.nama}` : lokasiGudang,
            jenis: isJenis1 ? '1 = Laporan Penjualan & Retur' : '2 = Laporan Pembelian & Retur',
            periode: `${p1} s.d. ${p2}`,
            time: new Date().toLocaleTimeString('id-ID'),
            rowCount: rows.length
          });
          setDownloadSuccess(true);
        }
      }

      clearInterval(progressInterval);
      setProgressVal(100);
      setProgressStatusText(`Selesai! Berhasil memuat ${rows.length} rincian transaksi.`);

    } catch (err) {
      clearInterval(progressInterval);
      console.error(`Error pada pemrosesan ${reportTitle}:`, err);
      const rawMessage = err.message || 'Terjadi kesalahan komunikasi dengan backend.';
      setErrorMsg(rawMessage);
      setErrorDetails({
        endpoint: `/${endpointData} & /${endpointExcel}`,
        loklap: lokasiGudang,
        periode: `${p1} s.d. ${p2}`,
        url: dataUrl,
        rawError: rawMessage
      });
      setProgressVal(0);
      setProgressStatusText('');
    } finally {
      setIsLoading(false);
    }
  };


  // Handler Download Ulang Berkas Excel yang telah digenerate
  const handleUnduhUlang = () => {
    if (downloadUrlRef.current && downloadedFileInfo) {
      const a = document.createElement('a');
      a.href = downloadUrlRef.current;
      a.download = downloadedFileInfo.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      handleProsesLaporan();
    }
  };

  // Handler Tutup (kembali ke dashboard)
  const handleTutup = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/home');
    }
  };

  // Ambil data aktif: gunakan data riil dari backend (reportData) jika tersedia, fallback ke mock jika belum proses
  const rawData = useMemo(() => {
    if (reportData && reportData.length > 0) {
      return reportData;
    }
    return activeReportType === '1' ? MOCK_PENJUALAN_DATA : MOCK_PEMBELIAN_DATA;
  }, [reportData, activeReportType]);

  // Filter data berdasarkan search text & tab tipe
  const filteredData = useMemo(() => {
    const q = searchFilter.toLowerCase().trim();
    return rawData.filter(item => {
      const tipeStr = (item.tipe || '').toLowerCase();
      if (filterTipe !== 'ALL') {
        if (filterTipe === 'UTAMA' && tipeStr.includes('retur')) return false;
        if (filterTipe === 'RETUR' && !tipeStr.includes('retur')) return false;
      }
      if (!q) return true;
      if (activeReportType === '1') {
        return (
          (item.no_bukti || '').toLowerCase().includes(q) ||
          (item.no_nota || '').toLowerCase().includes(q) ||
          (item.nama_pasien || '').toLowerCase().includes(q) ||
          (item.no_rm || '').toLowerCase().includes(q) ||
          (item.kd_obat || '').toLowerCase().includes(q) ||
          (item.nm_obat || '').toLowerCase().includes(q) ||
          (item.satuan || '').toLowerCase().includes(q) ||
          (item.dokter || '').toLowerCase().includes(q) ||
          (item.cara_bayar || '').toLowerCase().includes(q)
        );
      } else {
        return (
          (item.no_faktur || '').toLowerCase().includes(q) ||
          (item.no_ttb || '').toLowerCase().includes(q) ||
          (item.no_bukti || '').toLowerCase().includes(q) ||
          (item.no_sp || '').toLowerCase().includes(q) ||
          (item.no_po || '').toLowerCase().includes(q) ||
          (item.nama_pbf || '').toLowerCase().includes(q) ||
          (item.kode_pbf || '').toLowerCase().includes(q) ||
          (item.kd_obat || '').toLowerCase().includes(q) ||
          (item.nm_obat || '').toLowerCase().includes(q) ||
          (item.satuan || '').toLowerCase().includes(q) ||
          (item.status_bayar || '').toLowerCase().includes(q)
        );
      }
    });
  }, [rawData, searchFilter, filterTipe, activeReportType]);

  // Kalkulasi Summary / KPI
  const summary = useMemo(() => {
    // Jika tidak sedang difilter dan ada summary dari backend, gunakan backendSummary
    if (backendSummary && filterTipe === 'ALL' && !searchFilter && reportData.length > 0) {
      if (activeReportType === '1') {
        return {
          totalBruto: backendSummary.total_bruto || 0,
          totalDiskon: backendSummary.total_diskon || 0,
          totalRetur: backendSummary.total_retur || 0,
          totalNetto: backendSummary.total_netto || 0,
          countTransaksi: backendSummary.count_transaksi || 0,
          countRetur: backendSummary.count_retur || 0
        };
      } else {
        return {
          totalBeli: backendSummary.total_beli || 0,
          totalPpn: backendSummary.total_ppn || 0,
          totalRetur: backendSummary.total_retur || 0,
          totalNetto: backendSummary.total_netto || 0,
          countFaktur: backendSummary.count_faktur || 0,
          countRetur: backendSummary.count_retur || 0
        };
      }
    }

    // Kalkulasi reaktif dari filteredData
    if (activeReportType === '1') {
      const totalBruto = filteredData.reduce((acc, it) => acc + (it.bruto !== undefined ? it.bruto : (it.subtotal || 0)), 0);
      const totalDiskon = filteredData.reduce((acc, it) => acc + (it.diskon || 0), 0);
      const totalRetur = filteredData.reduce((acc, it) => acc + (it.retur || 0), 0);
      const totalNetto = filteredData.reduce((acc, it) => acc + (it.netto !== undefined ? it.netto : (it.total || 0)), 0);
      const countTransaksi = filteredData.filter(it => (it.tipe || '').toLowerCase() === 'penjualan').length;
      const countRetur = filteredData.filter(it => (it.tipe || '').toLowerCase().includes('retur')).length;
      return { totalBruto, totalDiskon, totalRetur, totalNetto, countTransaksi, countRetur };
    } else {
      const totalBeli = filteredData.reduce((acc, it) => acc + (it.dpp !== undefined ? it.dpp : (it.subtotal || 0)), 0);
      const totalPpn = filteredData.reduce((acc, it) => acc + (it.ppn || 0), 0);
      const totalRetur = filteredData.reduce((acc, it) => acc + (it.retur || 0), 0);
      const totalNetto = filteredData.reduce((acc, it) => acc + (it.netto !== undefined ? it.netto : (it.total || 0)), 0);
      const countFaktur = filteredData.filter(it => (it.tipe || '').toLowerCase() === 'pembelian').length;
      const countRetur = filteredData.filter(it => (it.tipe || '').toLowerCase().includes('retur')).length;
      return { totalBeli, totalPpn, totalRetur, totalNetto, countFaktur, countRetur };
    }
  }, [filteredData, activeReportType, backendSummary, filterTipe, searchFilter, reportData.length]);

  // Pagination Slice
  const totalPages = Math.ceil(filteredData.length / pageSize) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  // Lokasi Label aktif
  const selectedLokasiLabel = useMemo(() => {
    return LOKASI_OPTIONS.find(l => l.kode === activeLokasi)?.nama || activeLokasi;
  }, [activeLokasi]);

  // Handler Export CSV / Excel untuk preview
  const handleExportCSV = () => {
    if (filteredData.length === 0) return;
    let csvContent = "\uFEFF"; // BOM UTF-8 agar Excel mengenali format karakter dengan benar
    if (activeReportType === '1') {
      csvContent += "No,No Bukti,Tanggal,No RM,Nama Pasien,Kode Obat,Nama Obat,Satuan,Qty,Harga,Diskon,DPP,PPN,Total Netto,Tipe\r\n";
      filteredData.forEach((row, idx) => {
        csvContent += `${idx + 1},"${row.no_bukti || row.no_nota || ''}","${row.tanggal || ''}","${row.no_rm || ''}","${(row.nama_pasien || '').replace(/"/g, '""')}","${row.kd_obat || ''}","${(row.nm_obat || '').replace(/"/g, '""')}","${row.satuan || ''}",${row.qty || 0},${row.harga || 0},${row.diskon || 0},${row.dpp || 0},${row.ppn || 0},${row.netto !== undefined ? row.netto : (row.total || 0)},"${row.tipe || ''}"\r\n`;
      });
    } else {
      csvContent += "No,No TTB,No Faktur,No SP,Tanggal,Distributor PBF,Kode Obat,Nama Obat,Satuan,Qty,Harga,Diskon,DPP,PPN,Total Netto,Status Bayar,Tipe\r\n";
      filteredData.forEach((row, idx) => {
        csvContent += `${idx + 1},"${row.no_ttb || row.no_bukti || ''}","${row.no_faktur || ''}","${row.no_sp || row.no_po || ''}","${row.tanggal || ''}","${(row.nama_pbf || '').replace(/"/g, '""')}","${row.kd_obat || ''}","${(row.nm_obat || '').replace(/"/g, '""')}","${row.satuan || ''}",${row.qty || 0},${row.harga || 0},${row.diskon || 0},${row.dpp || row.subtotal || 0},${row.ppn || 0},${row.netto !== undefined ? row.netto : (row.total || 0)},"${row.status_bayar || ''}","${row.tipe || ''}"\r\n`;
      });
    }
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `Rincian_Keuangan_Apotek_${activeReportType === '1' ? 'Penjualan' : 'Pembelian'}_${periodeAwal}_sd_${periodeAkhir}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handler Cetak / Print
  const handlePrint = () => {
    window.print();
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f1f5f9',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, sans-serif"
    }}>

      {/* ── TOPBAR NAVIGASI SIMRS2 (NON-PRINT) ── */}
      <header className="d-print-none shadow-sm" style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        color: '#ffffff',
        padding: '8px 20px',
        borderBottom: '2px solid #0284c7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={handleTutup}
            className="btn btn-sm btn-outline-light d-flex align-items-center gap-1"
            style={{ borderRadius: '6px', fontSize: '0.78rem', padding: '4px 10px' }}
            title="Kembali ke halaman sebelumnya"
          >
            <i className="bi bi-arrow-left"></i>
            <span>Kembali</span>
          </button>
          <div style={{ width: '1px', height: '22px', background: 'rgba(255,255,255,0.25)' }}></div>
          <div className="d-flex align-items-center gap-2">
            <div style={{ background: '#0284c7', color: '#fff', padding: '4px 8px', borderRadius: '6px', fontSize: '0.85rem' }}>
              <i className="bi bi-hospital"></i>
            </div>
            <div>
              <div style={{ fontSize: '0.92rem', fontWeight: 700, letterSpacing: '0.3px', lineHeight: 1.2 }}>
                SIMRS2 &bull; Laporan Penjualan &amp; Pembelian Gudang Apotek
              </div>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                Modul Keuangan &amp; Akuntansi Logistik Farmasi (/LapApotekFinance)
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-3" style={{ fontSize: '0.76rem', color: '#cbd5e1' }}>
          <div className="d-none d-md-flex align-items-center gap-1">
            <i className="bi bi-calendar3 text-info"></i>
            <span>{time.date}</span>
            <span className="ms-1 fw-bold text-white">{time.clock}</span>
          </div>
          <div className="d-flex align-items-center gap-2" style={{ background: 'rgba(255,255,255,0.08)', padding: '3px 10px', borderRadius: '20px' }}>
            <i className="bi bi-person-circle text-warning"></i>
            <span className="fw-semibold text-white">{username}</span>
          </div>
        </div>
      </header>

      {/* ── KONTEN UTAMA ── */}
      <Container fluid className="p-3 flex-grow-1" style={{ maxWidth: '1600px' }}>

        {/* ══════════════════════════════════════════════════════════════════
            CARD FILTER: SESUAI PERSIS DENGAN GAMBAR DESAIN UI/UX PENGGUNA
            Header: Dark slate navy (#263238 / #1e293b) + Icon Table + "Laporan Keuangan"
            Body: 4 Input (Lokasi Gudang, Jenis Laporan, Periode, S.d.) + 2 Tombol (Proses Laporan, Tutup)
            ══════════════════════════════════════════════════════════════════ */}
        <div className="d-print-none mb-3 shadow-sm" style={{
          borderRadius: '4px',
          border: '1px solid #cbd5e1',
          overflow: 'hidden',
          background: '#ffffff'
        }}>
          {/* Header Bar Laporan Keuangan */}
          <div style={{
            background: '#263238', // Warna gelap navy/slate persis di gambar
            color: '#ffffff',
            padding: '7px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '0.88rem',
            fontWeight: 600,
            borderBottom: '1px solid #1e293b'
          }}>
            <i className="bi bi-grid-3x3-gap-fill" style={{ fontSize: '0.95rem' }}></i>
            <span>Laporan Keuangan</span>
          </div>

          {/* Form Filter Row */}
          <div style={{ padding: '12px 14px 10px 14px', background: '#ffffff' }}>
            <div className="row g-2 align-items-center">
              {/* 1. Lokasi Gudang */}
              <div className="col-12 col-md-3">
                <label style={{ fontSize: '0.78rem', color: '#1e293b', marginBottom: '3px', display: 'block', fontWeight: 500 }}>
                  Lokasi Gudang <span style={{ color: '#ef4444', fontWeight: 'bold' }}>*</span>
                </label>
                <select
                  className="form-select form-select-sm"
                  value={lokasiGudang}
                  onChange={(e) => setLokasiGudang(e.target.value)}
                  style={{
                    fontSize: '0.82rem',
                    borderRadius: '3px',
                    borderColor: '#cbd5e1',
                    height: '32px',
                    color: '#1e293b'
                  }}
                >
                  {LOKASI_OPTIONS.map((item) => (
                    <option key={item.kode} value={item.kode}>
                      {item.kode} = {item.nama}
                    </option>
                  ))}
                </select>
              </div>

              {/* 2. Jenis Laporan */}
              <div className="col-12 col-md-3">
                <label style={{ fontSize: '0.78rem', color: '#1e293b', marginBottom: '3px', display: 'block', fontWeight: 500 }}>
                  Jenis Laporan <span style={{ color: '#ef4444', fontWeight: 'bold' }}>*</span>
                </label>
                <select
                  className="form-select form-select-sm"
                  value={jenisLaporan}
                  onChange={(e) => setJenisLaporan(e.target.value)}
                  style={{
                    fontSize: '0.82rem',
                    borderRadius: '3px',
                    borderColor: '#cbd5e1',
                    height: '32px',
                    color: '#1e293b'
                  }}
                >
                  {JENIS_LAPORAN_OPTIONS.map((item) => (
                    <option key={item.kode} value={item.kode}>
                      {item.kode} = {item.nama}
                    </option>
                  ))}
                </select>
              </div>

              {/* 3. Periode (Tanggal Awal) */}
              <div className="col-12 col-md-3">
                <label style={{ fontSize: '0.78rem', color: '#1e293b', marginBottom: '3px', display: 'block', fontWeight: 500 }}>
                  Periode <span style={{ color: '#ef4444', fontWeight: 'bold' }}>*</span>
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={periodeAwal}
                  onChange={(e) => setPeriodeAwal(e.target.value)}
                  style={{
                    fontSize: '0.82rem',
                    borderRadius: '3px',
                    borderColor: '#cbd5e1',
                    height: '32px',
                    color: '#1e293b'
                  }}
                />
              </div>

              {/* 4. S.d. (Tanggal Akhir) */}
              <div className="col-12 col-md-3">
                <label style={{ fontSize: '0.78rem', color: '#1e293b', marginBottom: '3px', display: 'block', fontWeight: 500 }}>
                  S.d. <span style={{ color: '#ef4444', fontWeight: 'bold' }}>*</span>
                </label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={periodeAkhir}
                  onChange={(e) => setPeriodeAkhir(e.target.value)}
                  style={{
                    fontSize: '0.82rem',
                    borderRadius: '3px',
                    borderColor: '#cbd5e1',
                    height: '32px',
                    color: '#1e293b'
                  }}
                />
              </div>
            </div>

            {/* Tombol Aksi di Kanan Bawah Form */}
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 pt-2 border-top" style={{ borderColor: '#f1f5f9' }}>
              <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.72rem', color: '#64748b' }}>
                <i className="bi bi-hdd-network text-primary"></i>
                <span>Target Backend: <strong className="text-dark font-monospace">{getApiUrl()}</strong> ({String(jenisLaporan) === '1' ? '/lapjual_apotek' : '/lapbeli_apotek'})</span>
              </div>

              <div className="d-flex align-items-center gap-2">
                {/* Tombol Unduh Langsung via Browser Tab (Fallback jika fetch lambat/dibatasi) */}
                <button
                  type="button"
                  onClick={handleDirectDownload}
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                  title="Unduh langsung berkas Excel melalui jendela/tab baru browser"
                  style={{
                    fontSize: '0.75rem',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontWeight: 500
                  }}
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  <span>Unduh Langsung</span>
                </button>

                {/* Tombol Proses Laporan (Biru Muda / Cyan persis di gambar) */}
                <button
                  type="button"
                  onClick={handleProsesLaporan}
                  disabled={isLoading}
                  style={{
                    background: '#46b8da', // Warna cyan/teal muda persis tombol di gambar
                    color: '#ffffff',
                    border: '1px solid #31b0d5',
                    borderRadius: '4px',
                    padding: '5px 14px',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                  }}
                >
                  {isLoading ? (
                    <>
                      <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px', borderWidth: '1.5px' }} />
                      <span>Memproses...</span>
                    </>
                  ) : (
                    <>
                      <i className="bi bi-save2"></i>
                      <span>Proses Laporan</span>
                    </>
                  )}
                </button>

                {/* Tombol Tutup (Merah Magenta persis di gambar) */}
                <button
                  type="button"
                  onClick={handleTutup}
                  style={{
                    background: '#d9534f', // Warna merah maroon/danger persis tombol di gambar
                    color: '#ffffff',
                    border: '1px solid #d43f3a',
                    borderRadius: '4px',
                    padding: '5px 14px',
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
                  }}
                >
                  <i className="bi bi-box-arrow-right"></i>
                  <span>Tutup</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            PROGRESS BAR PEMROSESAN EXCEL KE BACKEND
            ══════════════════════════════════════════════════════════════════ */}
        {isLoading && (
          <div className="card shadow-sm border-0 mb-3 p-3 bg-white" style={{ borderRadius: '6px' }}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold text-dark d-flex align-items-center gap-2" style={{ fontSize: '0.82rem' }}>
                <i className="bi bi-file-earmark-excel-fill text-success fs-6"></i>
                <span>Memproses Ekspor Laporan {String(jenisLaporan) === '1' ? 'Penjualan' : 'Pembelian'} ke Backend ({String(jenisLaporan) === '1' ? '/lapjual_apotek' : '/lapbeli_apotek'})</span>
              </span>
              <span className="badge bg-primary font-monospace" style={{ fontSize: '0.74rem' }}>
                {progressVal}%
              </span>
            </div>
            <ProgressBar
              animated
              striped
              variant={progressVal === 100 ? 'success' : 'info'}
              now={progressVal}
              style={{ height: '14px', borderRadius: '4px' }}
            />
            <div className="mt-2 d-flex align-items-center gap-2 text-muted" style={{ fontSize: '0.74rem' }}>
              <Spinner animation="border" size="sm" style={{ width: '11px', height: '11px', borderWidth: '1.5px' }} />
              <span>{progressStatusText || 'Sedang memproses...'}</span>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            NOTIFIKASI SUKSES UNDUH FILE EXCEL
            ══════════════════════════════════════════════════════════════════ */}
        {downloadSuccess && downloadedFileInfo && (
          <div className="alert alert-success d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 py-2 px-3 shadow-sm" style={{ borderRadius: '6px', fontSize: '0.78rem' }}>
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-check-circle-fill text-success fs-5"></i>
              <div>
                <strong>Laporan Excel Berhasil Digenerate &amp; Diunduh:</strong>{' '}
                <span className="font-monospace fw-bold text-primary">{downloadedFileInfo.fileName}</span>{' '}
                ({downloadedFileInfo.sizeKb} KB) &bull; Jenis: <em>{downloadedFileInfo.jenis}</em> &bull; Lokasi: <em>{downloadedFileInfo.lokasi}</em> &bull; Periode: <em>{downloadedFileInfo.periode}</em>
              </div>
            </div>
            <button
              onClick={handleUnduhUlang}
              className="btn btn-sm btn-success d-flex align-items-center gap-1 shadow-sm"
              style={{ fontSize: '0.74rem', padding: '4px 12px', borderRadius: '4px', fontWeight: 600 }}
              title="Unduh ulang file Excel yang telah dibuat"
            >
              <i className="bi bi-download"></i>
              <span>Unduh Ulang File Excel</span>
            </button>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            REFACTORING: NOTIFIKASI & PANEL DETAIL PENANGANAN ERROR
            ══════════════════════════════════════════════════════════════════ */}
        {errorMsg && (
          <div className="card border-0 mb-3 shadow-sm" style={{ borderRadius: '6px', background: '#fef2f2', borderLeft: '5px solid #ef4444' }}>
            <div className="card-body p-3">
              <div className="d-flex align-items-start justify-content-between gap-2">
                <div className="d-flex align-items-start gap-2">
                  <i className="bi bi-exclamation-octagon-fill text-danger fs-4 flex-shrink-0 mt-1"></i>
                  <div>
                    <h6 className="fw-bold text-danger mb-1" style={{ fontSize: '0.88rem' }}>
                      Gagal Memproses Laporan Excel ke Backend
                    </h6>
                    <div className="text-dark fw-semibold mb-2" style={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                      {errorMsg}
                    </div>

                    {errorDetails && (
                      <div className="p-2 rounded bg-white border border-danger-subtle mb-2 font-monospace" style={{ fontSize: '0.72rem', color: '#7f1d1d' }}>
                        <div><strong>Target Endpoint:</strong> {errorDetails.endpoint}</div>
                        <div><strong>Parameter Lokasi (loklap):</strong> {errorDetails.loklap} &bull; <strong>Periode:</strong> {errorDetails.periode}</div>
                        <div className="text-truncate"><strong>URL Request:</strong> {errorDetails.url}</div>
                      </div>
                    )}

                    <div className="text-muted mb-2" style={{ fontSize: '0.72rem' }}>
                      <i className="bi bi-lightbulb-fill text-warning me-1"></i>
                      <strong>Saran Pemecahan Masalah:</strong> Pastikan service backend <code>simrsbackend</code> sedang aktif di <code>{getApiUrl() || 'http://localhost:8000'}</code>, koneksi database apotek berjalan normal, dan format periode tanggal terisi dengan benar.
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <button
                        onClick={handleProsesLaporan}
                        className="btn btn-sm btn-danger d-flex align-items-center gap-1 shadow-sm px-3"
                        style={{ fontSize: '0.75rem', fontWeight: 600, borderRadius: '4px' }}
                      >
                        <i className="bi bi-arrow-clockwise"></i>
                        <span>Coba Lagi (Retry)</span>
                      </button>
                      <button
                        onClick={handleDirectDownload}
                        className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 shadow-sm px-3"
                        style={{ fontSize: '0.75rem', fontWeight: 600, borderRadius: '4px' }}
                        title="Buka atau unduh langsung file laporan melalui tab/jendela baru browser"
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                        <span>Unduh Langsung (Direct Link)</span>
                      </button>
                      <button
                        onClick={() => { setErrorMsg(''); setErrorDetails(null); }}
                        className="btn btn-sm btn-outline-secondary px-3"
                        style={{ fontSize: '0.75rem', borderRadius: '4px' }}
                      >
                        Tutup Pesan
                      </button>
                    </div>

                  </div>
                </div>
                <button
                  onClick={() => { setErrorMsg(''); setErrorDetails(null); }}
                  className="btn-close"
                  style={{ fontSize: '0.65rem' }}
                  title="Tutup pesan error"
                ></button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            AREA PRATINJAU DESAIN LAPORAN (UI/UX)
            ══════════════════════════════════════════════════════════════════ */}
        {!hasProcessed && !isLoading ? (
          /* Placeholder sebelum klik Proses Laporan */
          <div className="card shadow-sm border-0 text-center py-5 px-3" style={{ borderRadius: '8px', background: '#ffffff' }}>
            <div className="mb-3">
              <div style={{
                width: '64px',
                height: '64px',
                background: '#e0f2fe',
                color: '#0284c7',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.75rem'
              }}>
                <i className="bi bi-receipt-cutoff"></i>
              </div>
            </div>
            <h5 className="fw-bold text-dark mb-1" style={{ fontSize: '1.05rem' }}>
              Silakan Tentukan Parameter Filter Laporan Keuangan
            </h5>
            <p className="text-muted mx-auto mb-3" style={{ fontSize: '0.82rem', maxWidth: '640px' }}>
              Pilih <strong>Lokasi Gudang</strong>, <strong>Jenis Laporan</strong> (1 = Penjualan &amp; Retur atau 2 = Pembelian &amp; Retur), serta rentang <strong>Periode</strong>, kemudian tekan tombol <span className="badge bg-info text-white">Proses Laporan</span>. Sistem akan otomatis memanggil endpoint backend yang sesuai dan mengunduh berkas laporan dalam format Excel (.xlsx).
            </p>
            <div className="d-flex justify-content-center gap-2 flex-wrap">
              <span className="badge bg-light text-secondary border px-2 py-1" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-file-earmark-excel text-success me-1"></i>Jenis 1: /lapjual_apotek
              </span>
              <span className="badge bg-light text-secondary border px-2 py-1" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-file-earmark-excel text-primary me-1"></i>Jenis 2: /lapbeli_apotek
              </span>
              <span className="badge bg-light text-secondary border px-2 py-1" style={{ fontSize: '0.72rem' }}>
                <i className="bi bi-check-circle-fill text-success me-1"></i>Format Excel Otomatis
              </span>
            </div>
          </div>
        ) : (
          /* Area Hasil Pemrosesan Laporan */
          <div className="animate__animated animate__fadeIn">

            {/* 1. Baris Informasi Ringkasan & KPI Cards */}
            <div className="row g-2 mb-3">
              {activeReportType === '1' ? (
                /* KPI Laporan Penjualan & Retur */
                <>
                  <div className="col-12 col-sm-6 col-md-3">
                    <div className="card border-0 shadow-sm p-3 h-100" style={{ borderRadius: '8px', background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', borderLeft: '4px solid #3b82f6' }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ fontSize: '0.72rem', color: '#1e40af', fontWeight: 600 }}>TRANSAKSI PENJUALAN</span>
                        <i className="bi bi-cart-check-fill text-primary"></i>
                      </div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e3a8a' }}>
                        {summary.countTransaksi} <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Faktur</span>
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#3b82f6' }}>
                        Retur: {summary.countRetur} transaksi
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-md-3">
                    <div className="card border-0 shadow-sm p-3 h-100" style={{ borderRadius: '8px', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderLeft: '4px solid #10b981' }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ fontSize: '0.72rem', color: '#065f46', fontWeight: 600 }}>PENJUALAN BRUTO</span>
                        <i className="bi bi-cash-stack text-success"></i>
                      </div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#064e3b' }}>
                        Rp {fmtRupiah(summary.totalBruto)}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#059669' }}>
                        Diskon: Rp {fmtRupiah(summary.totalDiskon)}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-md-3">
                    <div className="card border-0 shadow-sm p-3 h-100" style={{ borderRadius: '8px', background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)', borderLeft: '4px solid #ef4444' }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ fontSize: '0.72rem', color: '#991b1b', fontWeight: 600 }}>TOTAL RETUR JUAL</span>
                        <i className="bi bi-arrow-counterclockwise text-danger"></i>
                      </div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#7f1d1d' }}>
                        Rp {fmtRupiah(summary.totalRetur)}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#dc2626' }}>
                        Pengurangan omset penjualan
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-md-3">
                    <div className="card border-0 shadow-sm p-3 h-100" style={{ borderRadius: '8px', background: 'linear-gradient(135deg, #ecfdf5 0%, #a7f3d0 100%)', borderLeft: '4px solid #059669' }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ fontSize: '0.72rem', color: '#064e3b', fontWeight: 700 }}>PENJUALAN NETTO</span>
                        <i className="bi bi-wallet2 text-success fs-5"></i>
                      </div>
                      <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#064e3b' }}>
                        Rp {fmtRupiah(summary.totalNetto)}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#047857', fontWeight: 600 }}>
                        (Bruto − Diskon − Retur)
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* KPI Laporan Pembelian & Retur */
                <>
                  <div className="col-12 col-sm-6 col-md-3">
                    <div className="card border-0 shadow-sm p-3 h-100" style={{ borderRadius: '8px', background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', borderLeft: '4px solid #8b5cf6' }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ fontSize: '0.72rem', color: '#5b21b6', fontWeight: 600 }}>TOTAL FAKTUR PBF</span>
                        <i className="bi bi-truck text-purple"></i>
                      </div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#4c1d95' }}>
                        {summary.countFaktur} <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Faktur</span>
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#7c3aed' }}>
                        Retur Beli: {summary.countRetur} dokumen
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-md-3">
                    <div className="card border-0 shadow-sm p-3 h-100" style={{ borderRadius: '8px', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderLeft: '4px solid #0284c7' }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ fontSize: '0.72rem', color: '#075985', fontWeight: 600 }}>PEMBELIAN BRUTO</span>
                        <i className="bi bi-box-seam text-info"></i>
                      </div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0c4a6e' }}>
                        Rp {fmtRupiah(summary.totalBeli)}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#0284c7' }}>
                        PPN Masukan: Rp {fmtRupiah(summary.totalPpn)}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-md-3">
                    <div className="card border-0 shadow-sm p-3 h-100" style={{ borderRadius: '8px', background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)', borderLeft: '4px solid #f97316' }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ fontSize: '0.72rem', color: '#9a3412', fontWeight: 600 }}>RETUR PEMBELIAN</span>
                        <i className="bi bi-arrow-left-right text-warning"></i>
                      </div>
                      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#7c2d12' }}>
                        Rp {fmtRupiah(summary.totalRetur)}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#ea580c' }}>
                        Pengurangan hutang ke PBF
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-md-3">
                    <div className="card border-0 shadow-sm p-3 h-100" style={{ borderRadius: '8px', background: 'linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)', borderLeft: '4px solid #0d9488' }}>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span style={{ fontSize: '0.72rem', color: '#115e59', fontWeight: 700 }}>HUTANG / NETTO PEMBELIAN</span>
                        <i className="bi bi-bank text-teal fs-5"></i>
                      </div>
                      <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#134e4a' }}>
                        Rp {fmtRupiah(summary.totalNetto)}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#0f766e', fontWeight: 600 }}>
                        (Subtotal + PPN − Retur)
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* 2. Card Tabel Rincian Data */}
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '8px', overflow: 'hidden' }}>
              {/* Header Tabel & Toolbar Aksi */}
              <div className="p-3 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: '0.94rem' }}>
                      {activeReportType === '1' ? 'Rincian Penjualan & Retur Obat' : 'Rincian Pembelian & Retur Obat PBF'}
                    </h6>
                    <Badge bg="primary" style={{ fontSize: '0.68rem' }}>
                      {selectedLokasiLabel}
                    </Badge>
                    <Badge bg="secondary" style={{ fontSize: '0.68rem' }}>
                      {fmtDateDisplay(periodeAwal)} s.d. {fmtDateDisplay(periodeAkhir)}
                    </Badge>
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                    Menampilkan {filteredData.length} baris data laporan
                  </div>
                </div>

                {/* Toolbar Pencarian & Export */}
                <div className="d-flex align-items-center gap-2 flex-wrap d-print-none">
                  {/* Filter Sub-Tipe */}
                  <div className="btn-group btn-group-sm" role="group">
                    <button
                      type="button"
                      className={`btn ${filterTipe === 'ALL' ? 'btn-dark' : 'btn-outline-secondary'}`}
                      style={{ fontSize: '0.74rem' }}
                      onClick={() => setFilterTipe('ALL')}
                    >
                      Semua ({rawData.length})
                    </button>
                    <button
                      type="button"
                      className={`btn ${filterTipe === 'UTAMA' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      style={{ fontSize: '0.74rem' }}
                      onClick={() => setFilterTipe('UTAMA')}
                    >
                      {activeReportType === '1' ? 'Penjualan Saja' : 'Pembelian Saja'}
                    </button>
                    <button
                      type="button"
                      className={`btn ${filterTipe === 'RETUR' ? 'btn-danger' : 'btn-outline-secondary'}`}
                      style={{ fontSize: '0.74rem' }}
                      onClick={() => setFilterTipe('RETUR')}
                    >
                      Retur Saja
                    </button>
                  </div>

                  {/* Input Search */}
                  <div style={{ position: 'relative', width: '220px' }}>
                    <i className="bi bi-search text-muted" style={{ position: 'absolute', left: '9px', top: '7px', fontSize: '0.75rem' }}></i>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Cari transaksi..."
                      value={searchFilter}
                      onChange={(e) => { setSearchFilter(e.target.value); setCurrentPage(1); }}
                      style={{ paddingLeft: '28px', fontSize: '0.76rem', borderRadius: '6px' }}
                    />
                    {searchFilter && (
                      <button
                        onClick={() => setSearchFilter('')}
                        style={{ position: 'absolute', right: '6px', top: '4px', border: 'none', background: 'transparent', color: '#94a3b8', fontSize: '0.75rem' }}
                      >
                        <i className="bi bi-x-circle-fill"></i>
                      </button>
                    )}
                  </div>

                  {/* Tombol Export CSV */}
                  <button
                    onClick={handleExportCSV}
                    className="btn btn-sm btn-success d-flex align-items-center gap-1"
                    style={{ borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600 }}
                    title="Export data ke format CSV / Excel"
                  >
                    <i className="bi bi-file-earmark-excel"></i>
                    <span>Export CSV</span>
                  </button>

                  {/* Tombol Cetak */}
                  <button
                    onClick={handlePrint}
                    className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                    style={{ borderRadius: '6px', fontSize: '0.75rem' }}
                    title="Cetak Laporan ke PDF / Printer"
                  >
                    <i className="bi bi-printer"></i>
                    <span>Cetak</span>
                  </button>
                </div>
              </div>

              {/* Tabel Responsif */}
              <div className="table-responsive">
                {activeReportType === '1' ? (
                  /* ── TABEL 1: RINCIAN PENJUALAN & RETUR OBAT ── */
                  <Table hover size="sm" className="mb-0 align-middle" style={{ fontSize: '0.75rem' }}>
                    <thead style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1', color: '#334155' }}>
                      <tr>
                        <th style={{ width: '40px', textAlign: 'center' }}>No</th>
                        <th style={{ width: '130px' }}>No. Bukti / Nota</th>
                        <th style={{ width: '85px' }}>Tanggal</th>
                        <th style={{ width: '75px' }}>No. RM</th>
                        <th style={{ minWidth: '150px' }}>Nama Pasien</th>
                        <th style={{ minWidth: '180px' }}>Nama Obat / Alkes</th>
                        <th style={{ width: '65px', textAlign: 'center' }}>Satuan</th>
                        <th style={{ width: '55px', textAlign: 'right' }}>Qty</th>
                        <th style={{ width: '85px', textAlign: 'right' }}>Harga (Rp)</th>
                        <th style={{ width: '75px', textAlign: 'right' }}>Diskon</th>
                        <th style={{ width: '85px', textAlign: 'right' }}>DPP (Rp)</th>
                        <th style={{ width: '75px', textAlign: 'right' }}>PPN (Rp)</th>
                        <th style={{ width: '100px', textAlign: 'right' }}>Total Netto (Rp)</th>
                        <th style={{ width: '85px', textAlign: 'center' }}>Tipe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.length === 0 ? (
                        <tr>
                          <td colSpan={14} className="text-center py-4 text-muted">
                            <i className="bi bi-inbox fs-4 d-block mb-1"></i>
                            Tidak ada data transaksi rincian penjualan yang sesuai dengan filter.
                          </td>
                        </tr>
                      ) : (
                        paginatedData.map((row, idx) => {
                          const isRetur = (row.tipe || '').toLowerCase().includes('retur');
                          const itemNetto = row.netto !== undefined ? row.netto : (row.total || 0);
                          return (
                            <tr key={row.id || idx} style={{ background: isRetur ? '#fef2f2' : undefined }}>
                              <td className="text-center text-muted">{(currentPage - 1) * pageSize + idx + 1}</td>
                              <td className="font-monospace fw-bold text-primary">{row.no_bukti || row.no_nota || '-'}</td>
                              <td>{fmtDateDisplay(row.tanggal)}</td>
                              <td className="font-monospace text-secondary">{row.no_rm || '-'}</td>
                              <td className="fw-semibold text-dark">{row.nama_pasien || '-'}</td>
                              <td>
                                <div className="fw-semibold text-dark">{row.nm_obat || '-'}</div>
                                <div className="text-muted font-monospace" style={{ fontSize: '0.67rem' }}>{row.kd_obat || ''}</div>
                              </td>
                              <td className="text-center text-muted">{row.satuan || '-'}</td>
                              <td className="text-end font-monospace">{row.qty || 0}</td>
                              <td className="text-end font-monospace">{fmtRupiah(row.harga || 0)}</td>
                              <td className="text-end font-monospace text-muted">{fmtRupiah(row.diskon || 0)}</td>
                              <td className="text-end font-monospace">{fmtRupiah(row.dpp || 0)}</td>
                              <td className="text-end font-monospace text-muted">{fmtRupiah(row.ppn || 0)}</td>
                              <td className={`text-end font-monospace fw-bold ${itemNetto < 0 ? 'text-danger' : 'text-success'}`}>
                                {itemNetto < 0 ? `-${fmtRupiah(Math.abs(itemNetto))}` : fmtRupiah(itemNetto)}
                              </td>
                              <td className="text-center">
                                <Badge bg={isRetur ? 'danger' : 'success'} style={{ fontSize: '0.64rem' }}>
                                  {row.tipe || 'Penjualan'}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {/* Grand Total Row */}
                    {filteredData.length > 0 && (
                      <tfoot style={{ background: '#f8fafc', borderTop: '2px solid #cbd5e1', fontWeight: 'bold' }}>
                        <tr>
                          <td colSpan={8} className="text-end text-uppercase" style={{ fontSize: '0.74rem', color: '#1e293b' }}>
                            Ringkasan Total ({filteredData.length} baris):
                          </td>
                          <td className="text-end font-monospace text-dark">Rp {fmtRupiah(summary.totalBruto)}</td>
                          <td className="text-end font-monospace text-muted">Rp {fmtRupiah(summary.totalDiskon)}</td>
                          <td colSpan={2} className="text-end font-monospace text-danger">Retur: -Rp {fmtRupiah(summary.totalRetur)}</td>
                          <td className="text-end font-monospace text-success fw-bold" style={{ fontSize: '0.82rem' }}>
                            Rp {fmtRupiah(summary.totalNetto)}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </Table>
                ) : (
                  /* ── TABEL 2: RINCIAN PEMBELIAN & RETUR OBAT PBF ── */
                  <Table hover size="sm" className="mb-0 align-middle" style={{ fontSize: '0.75rem' }}>
                    <thead style={{ background: '#f8fafc', borderBottom: '2px solid #cbd5e1', color: '#334155' }}>
                      <tr>
                        <th style={{ width: '40px', textAlign: 'center' }}>No</th>
                        <th style={{ width: '130px' }}>No. TTB / Bukti</th>
                        <th style={{ width: '85px' }}>Tanggal</th>
                        <th style={{ width: '120px' }}>No. Faktur</th>
                        <th style={{ width: '110px' }}>No. SP / PO</th>
                        <th style={{ minWidth: '150px' }}>Distributor / PBF</th>
                        <th style={{ minWidth: '180px' }}>Nama Obat / Alkes</th>
                        <th style={{ width: '60px', textAlign: 'center' }}>Satuan</th>
                        <th style={{ width: '50px', textAlign: 'right' }}>Qty</th>
                        <th style={{ width: '80px', textAlign: 'right' }}>Harga</th>
                        <th style={{ width: '85px', textAlign: 'right' }}>DPP (Rp)</th>
                        <th style={{ width: '75px', textAlign: 'right' }}>PPN 11%</th>
                        <th style={{ width: '95px', textAlign: 'right' }}>Total (Rp)</th>
                        <th style={{ width: '85px', textAlign: 'center' }}>Status</th>
                        <th style={{ width: '75px', textAlign: 'center' }}>Tipe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.length === 0 ? (
                        <tr>
                          <td colSpan={15} className="text-center py-4 text-muted">
                            <i className="bi bi-inbox fs-4 d-block mb-1"></i>
                            Tidak ada data transaksi rincian pembelian yang sesuai dengan filter.
                          </td>
                        </tr>
                      ) : (
                        paginatedData.map((row, idx) => {
                          const isRetur = (row.tipe || '').toLowerCase().includes('retur');
                          const itemNetto = row.netto !== undefined ? row.netto : (row.total || 0);
                          return (
                            <tr key={row.id || idx} style={{ background: isRetur ? '#fff7ed' : undefined }}>
                              <td className="text-center text-muted">{(currentPage - 1) * pageSize + idx + 1}</td>
                              <td className="font-monospace fw-bold text-dark">{row.no_ttb || row.no_bukti || '-'}</td>
                              <td>{fmtDateDisplay(row.tanggal)}</td>
                              <td className="font-monospace text-primary">{row.no_faktur || '-'}</td>
                              <td className="font-monospace text-secondary" style={{ fontSize: '0.7rem' }}>{row.no_sp || row.no_po || '-'}</td>
                              <td>
                                <div className="fw-semibold text-dark">{row.nama_pbf || '-'}</div>
                                <div className="text-muted font-monospace" style={{ fontSize: '0.67rem' }}>{row.kode_pbf || ''}</div>
                              </td>
                              <td>
                                <div className="fw-semibold text-dark">{row.nm_obat || '-'}</div>
                                <div className="text-muted font-monospace" style={{ fontSize: '0.67rem' }}>{row.kd_obat || ''}</div>
                              </td>
                              <td className="text-center text-muted">{row.satuan || '-'}</td>
                              <td className="text-end font-monospace">{row.qty || 0}</td>
                              <td className="text-end font-monospace">{fmtRupiah(row.harga || 0)}</td>
                              <td className="text-end font-monospace">{fmtRupiah(row.dpp || row.subtotal || 0)}</td>
                              <td className="text-end font-monospace text-muted">{fmtRupiah(row.ppn || 0)}</td>
                              <td className={`text-end font-monospace fw-bold ${itemNetto < 0 ? 'text-danger' : 'text-teal'}`} style={{ color: itemNetto >= 0 ? '#0f766e' : undefined }}>
                                {itemNetto < 0 ? `-${fmtRupiah(Math.abs(itemNetto))}` : fmtRupiah(itemNetto)}
                              </td>
                              <td className="text-center">
                                <Badge bg={row.status_bayar === 'Lunas' ? 'success' : row.status_bayar === 'Kredit Nota' ? 'info' : 'danger'} style={{ fontSize: '0.62rem' }}>
                                  {row.status_bayar || 'Belum Lunas'}
                                </Badge>
                              </td>
                              <td className="text-center">
                                <Badge bg={isRetur ? 'warning' : 'primary'} text={isRetur ? 'dark' : 'white'} style={{ fontSize: '0.62rem' }}>
                                  {row.tipe || 'Pembelian'}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                    {/* Grand Total Row */}
                    {filteredData.length > 0 && (
                      <tfoot style={{ background: '#f8fafc', borderTop: '2px solid #cbd5e1', fontWeight: 'bold' }}>
                        <tr>
                          <td colSpan={10} className="text-end text-uppercase" style={{ fontSize: '0.74rem', color: '#1e293b' }}>
                            Grand Total Pembelian ({filteredData.length} baris):
                          </td>
                          <td className="text-end font-monospace text-dark">Rp {fmtRupiah(summary.totalBeli)}</td>
                          <td className="text-end font-monospace text-muted">Rp {fmtRupiah(summary.totalPpn)}</td>
                          <td className="text-end font-monospace fw-bold" style={{ fontSize: '0.82rem', color: '#0f766e' }}>
                            Rp {fmtRupiah(summary.totalNetto)}
                          </td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    )}
                  </Table>
                )}
              </div>

              {/* Footer Pagination */}
              {totalPages > 1 && (
                <div className="p-2 border-top bg-light d-flex justify-content-between align-items-center d-print-none">
                  <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                    Halaman {currentPage} dari {totalPages}
                  </div>
                  <Pagination size="sm" className="mb-0">
                    <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} />
                  </Pagination>
                </div>
              )}
            </div>

            {/* Banner Informasi Modul */}
            <div className="alert alert-info py-2 px-3 d-flex align-items-center gap-2 d-print-none shadow-sm" style={{ borderRadius: '6px', fontSize: '0.75rem' }}>
              <i className="bi bi-info-circle-fill text-info flex-shrink-0 fs-6"></i>
              <div>
                <strong>Informasi Modul (/LapApotekFinance):</strong> Tombol <em>Proses Laporan</em> terhubung langsung ke backend untuk ekspor Excel: Jenis <strong>1</strong> menggunakan endpoint <code className="text-dark bg-white px-1 rounded border">/lapjual_apotek</code> dan Jenis <strong>2</strong> menggunakan endpoint <code className="text-dark bg-white px-1 rounded border">/lapbeli_apotek</code>. Berkas Excel resmi langsung diunduh otomatis ke perangkat Anda.
              </div>
            </div>

          </div>
        )}

      </Container>
    </div>
  );
};

export default LapApotekFinance;
